import { lazy, Suspense } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { toast } from "sonner";
import { captureError } from "@/services/errorMonitorService";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/layout/ProtectedRoute";
import { ErrorBoundary } from "@/components/layout/ErrorBoundary";
import { ThemeProvider } from "@/components/theme-provider";
import { Loader2 } from "lucide-react";

// --- Lazy-loaded pages ---

// Public pages
const Index = lazy(() => import("./pages/Index"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const Login = lazy(() => import("./pages/Login"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const UpdatePassword = lazy(() => import("./pages/UpdatePassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const QuizPericia = lazy(() => import("./pages/QuizPericia"));

// Protected pages - Student
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Checkin = lazy(() => import("./pages/Checkin"));
const CheckinEdit = lazy(() => import("./pages/CheckinEdit"));
const CheckinV2 = lazy(() => import("./pages/CheckinV2Page"));
const CheckinDetail = lazy(() => import("./pages/CheckinDetail"));
const CheckinEvolution = lazy(() => import("./pages/CheckinEvolution"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Settings = lazy(() => import("./pages/Settings"));
const ChangePassword = lazy(() => import("./pages/ChangePassword"));
const FirstAccessPassword = lazy(() => import("./pages/FirstAccessPassword"));
const ClubeLivro = lazy(() => import("./pages/ClubeLivro"));
const SOSPage = lazy(() => import("./pages/sos/SOSPage"));
const StudentProfile = lazy(() => import("./pages/StudentProfile"));
const MyAttendance = lazy(() => import("./pages/MyAttendance"));
const MyCheckins = lazy(() => import("./pages/MyCheckins"));
const CommunityPage = lazy(() => import("./pages/Community"));
const SabatinaPage = lazy(() => import("./pages/sabatina/SabatinaPage"));
const HotseatPage = lazy(() => import("./pages/HotseatPage"));
const PartnerDashboard = lazy(() => import("./pages/PartnerDashboard"));
const Pontuacao = lazy(() => import("./pages/Pontuacao"));
const PlanetaAspirantePage = lazy(() =>
  import("./features/planeta-aspirante/PlanetaAspirantePage").then((m) => ({
    default: m.PlanetaAspirantePage,
  })),
);
const MeuPerfil = lazy(() => import("./pages/MeuPerfil"));
const ClientReportPage = lazy(() => import("./pages/ClientReportPage"));
const Sonhos = lazy(() => import("./pages/Sonhos"));
const Conteudo = lazy(() => import("./pages/Conteudo"));
const ConteudoAula = lazy(() => import("./pages/ConteudoAula"));
const AulasManagement = lazy(() => import("./pages/mentor/AulasManagement"));

// Revenue Goals System
const GoalDashboardPage = lazy(() => import("./pages/goals/GoalDashboardPage"));
const GoalSetupPage = lazy(() => import("./pages/goals/GoalSetupPage"));
const MonthlyInputPage = lazy(() => import("./pages/goals/MonthlyInputPage"));
const MonthlyReportPage = lazy(() => import("./pages/goals/MonthlyReportPage"));
const EditGoalPage = lazy(() => import("./pages/goals/EditGoalPage"));
const GoalArchivePage = lazy(() => import("./pages/goals/GoalArchivePage"));
const CompaniesPage = lazy(() => import("./pages/goals/CompaniesPage"));

// Protected pages - Mentor
const MentorDashboard = lazy(() => import("./pages/mentor/MentorDashboard"));
const StudentsList = lazy(() => import("./pages/mentor/StudentsList"));
const StudentDetail = lazy(() => import("./pages/mentor/StudentDetail"));
const ClubeLivroManager = lazy(() => import("./pages/mentor/ClubeLivroManager"));
const StudentsNetwork = lazy(() => import("./pages/mentor/StudentsNetwork"));
const SOSManagement = lazy(() => import("./pages/sos/SOSManagement"));
const SabatinaManagement = lazy(() => import("./pages/sabatina/SabatinaManagement"));
const AttendancePage = lazy(() => import("./pages/AttendancePage"));
const EagleVisionPage = lazy(() => import("./pages/mentor/EagleVision"));
const ManualCheckin = lazy(() => import("./pages/mentor/ManualCheckin"));
const EventsManager = lazy(() => import("./pages/mentor/EventsManager"));
const HotseatManagement = lazy(() => import("./pages/mentor/HotseatManagement"));
const QuizPericiaResultsPage = lazy(() => import("./pages/mentor/QuizPericiaResultsPage"));
const AdminMonthlyReports = lazy(() => import("./pages/admin/AdminMonthlyReports"));
const PromotionsManagement = lazy(() => import("./pages/mentor/PromotionsManagement"));
const DuplasAnjosManagement = lazy(() => import("./pages/mentor/DuplasAnjosManagement"));
const MentorAssignment = lazy(() => import("./pages/mentor/MentorAssignment"));

// Mentor Revenue Goals
const MentorGoalsOverviewPage = lazy(() => import("./pages/mentor/MentorGoalsOverviewPage"));
const StudentGoalDetailPage = lazy(() => import("./pages/mentor/StudentGoalDetailPage"));
const MentorGoalEditorPage = lazy(() => import("./pages/mentor/MentorGoalEditorPage"));
const NPSDashboard = lazy(() => import("./pages/mentor/NPSDashboard"));
const MentorMonthlyReports = lazy(() => import("./pages/mentor/MentorMonthlyReports"));
const CompareStudents = lazy(() => import("./pages/mentor/CompareStudents"));
const MentorReportPage = lazy(() => import("./pages/mentor/MentorReportPage"));

// Protected pages - Admin
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const UsersManagement = lazy(() => import("./pages/admin/UsersManagement"));
const ForensicDashboard = lazy(() => import("./pages/admin/ForensicDashboard"));
const AdminGoalsAnalyticsPage = lazy(() => import("./pages/admin/AdminGoalsAnalyticsPage"));
const ExecutiveDashboard = lazy(() => import("./pages/admin/ExecutiveDashboard"));
const MentorPerformance = lazy(() => import("./pages/admin/MentorPerformance"));
const CohortPage = lazy(() => import("./pages/admin/CohortPage"));
const ExecutiveReportPage = lazy(() => import("./pages/admin/ExecutiveReportPage"));
const SystemHealth = lazy(() => import("./pages/admin/SystemHealth"));
const GovernanceDashboard = lazy(() => import("./pages/admin/GovernanceDashboard"));

// --- Suspense fallback ---
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <p className="text-muted-foreground">Carregando...</p>
    </div>
  </div>
);

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      captureError(error, { context: 'query', queryKey: query?.queryKey as unknown as string[] });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Erro inesperado";
      toast.error(message, { description: "Tente novamente mais tarde." });
      captureError(error, { context: 'mutation' });
    },
  }),
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
      <TooltipProvider>
        <Sonner />
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AuthProvider>
            <ErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Public routes */}
                  <Route path="/" element={<Index />} />
                  <Route path="/landing" element={<LandingPage />} />
                  <Route path="/quiz-pericia" element={<QuizPericia />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/update-password" element={<UpdatePassword />} />

                  {/* Student routes */}
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/partner-dashboard"
                    element={
                      <ProtectedRoute>
                        <PartnerDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/checkin"
                    element={
                      <ProtectedRoute>
                        <Navigate to="/checkin-v2" replace />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/checkin-v2"
                    element={
                      <ProtectedRoute>
                        <CheckinV2 />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/checkin-v2/:id"
                    element={
                      <ProtectedRoute>
                        <CheckinV2 />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/checkin/:id"
                    element={
                      <ProtectedRoute>
                        <CheckinEdit />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/notifications"
                    element={
                      <ProtectedRoute>
                        <Notifications />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>
                        <Settings />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/change-password"
                    element={
                      <ProtectedRoute>
                        <ChangePassword />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/clube-livro"
                    element={
                      <ProtectedRoute>
                        <ClubeLivro />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/sos"
                    element={
                      <ProtectedRoute>
                        <SOSPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/sabatina"
                    element={
                      <ProtectedRoute>
                        <SabatinaPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/network"
                    element={
                      <ProtectedRoute>
                        <StudentsNetwork />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/profile/:id"
                    element={
                      <ProtectedRoute>
                        <StudentProfile />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/hotseat"
                    element={
                      <ProtectedRoute>
                        <HotseatPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my-attendance"
                    element={
                      <ProtectedRoute>
                        <MyAttendance />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my-checkins"
                    element={
                      <ProtectedRoute>
                        <MyCheckins />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my-checkins/:id"
                    element={
                      <ProtectedRoute>
                        <CheckinDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/checkin-evolution"
                    element={
                      <ProtectedRoute>
                        <CheckinEvolution />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/community"
                    element={
                      <ProtectedRoute>
                        <CommunityPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/meu-relatorio"
                    element={
                      <ProtectedRoute>
                        <ClientReportPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/conteudo"
                    element={
                      <ProtectedRoute>
                        <Conteudo />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/conteudo/:id"
                    element={
                      <ProtectedRoute>
                        <ConteudoAula />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/sonhos"
                    element={
                      <ProtectedRoute>
                        <Sonhos />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/pontuacao"
                    element={
                      <ProtectedRoute>
                        <Pontuacao />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/meu-perfil"
                    element={
                      <ProtectedRoute>
                        <MeuPerfil />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/planeta-aspirante"
                    element={
                      <ProtectedRoute>
                        <PlanetaAspirantePage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Revenue Goals Routes */}
                  <Route
                    path="/revenue-goals"
                    element={
                      <ProtectedRoute>
                        <GoalDashboardPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/revenue-goals/setup"
                    element={
                      <ProtectedRoute>
                        <GoalSetupPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/revenue-goals/monthly-input"
                    element={
                      <ProtectedRoute>
                        <MonthlyInputPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/revenue-goals/reports"
                    element={
                      <ProtectedRoute>
                        <MonthlyReportPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/revenue-goals/edit/:goalId"
                    element={
                      <ProtectedRoute>
                        <EditGoalPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/revenue-goals/archive"
                    element={
                      <ProtectedRoute>
                        <GoalArchivePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/revenue-goals/companies"
                    element={
                      <ProtectedRoute>
                        <CompaniesPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Mentor routes */}
                  <Route
                    path="/mentor"
                    element={
                      <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                        <MentorDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/mentor/eagle-vision"
                    element={
                      <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                        <EagleVisionPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/mentor/students"
                    element={
                      <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                        <StudentsList />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/mentor/student/:id"
                    element={
                      <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                        <StudentDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/mentor/clube-livro"
                    element={
                      <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                        <ClubeLivroManager />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/mentor/sos"
                    element={
                      <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                        <SOSManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/mentor/sabatina"
                    element={
                      <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                        <SabatinaManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/mentor/attendance"
                    element={
                      <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                        <AttendancePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/mentor/manual-checkin"
                    element={
                      <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                        <ManualCheckin />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/mentor/events"
                    element={
                      <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                        <EventsManager />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/mentor/hotseat"
                    element={
                      <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                        <HotseatManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/mentor/quiz-pericia"
                    element={
                      <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                        <QuizPericiaResultsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/mentor/promotions"
                    element={
                      <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                        <PromotionsManagement />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/mentor/aulas"
                    element={
                      <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                        <AulasManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/mentor/duplas-anjos"
                    element={
                      <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                        <DuplasAnjosManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/mentor/mentor-assignment"
                    element={
                      <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                        <MentorAssignment />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/mentor/nps"
                    element={
                      <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                        <NPSDashboard />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/mentor/reports"
                    element={
                      <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                        <MentorMonthlyReports />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/mentor/compare"
                    element={
                      <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                        <CompareStudents />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/mentor/meu-relatorio"
                    element={
                      <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                        <MentorReportPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Mentor Revenue Goals Routes */}
                  <Route
                    path="/mentor/revenue-goals"
                    element={
                      <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                        <MentorGoalsOverviewPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/mentor/revenue-goals/student/:id"
                    element={
                      <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                        <StudentGoalDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/mentor/revenue-goals/student/:id/setup"
                    element={
                      <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                        <MentorGoalEditorPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/mentor/revenue-goals/student/:id/edit/:goalId"
                    element={
                      <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                        <MentorGoalEditorPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Admin routes */}
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <AdminDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/users"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <UsersManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/forensic-dashboard"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <ForensicDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/sos"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <SOSManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/sabatina"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <SabatinaManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/revenue-goals"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <AdminGoalsAnalyticsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/quiz-pericia"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <QuizPericiaResultsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/reports"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <AdminMonthlyReports />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/executive"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <ExecutiveDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/mentor-performance"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <MentorPerformance />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/cohorts"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <CohortPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/executive-report"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <ExecutiveReportPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/system"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <SystemHealth />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/governance"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <GovernanceDashboard />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/first-access-password"
                    element={
                      <ProtectedRoute>
                        <FirstAccessPassword />
                      </ProtectedRoute>
                    }
                  />

                  {/* Catch all */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
